import { Avatar, Box, Divider, Stack, Typography, drawerClasses } from "@mui/material";
import { useEffect } from "react";
import { Link } from "react-router-dom";

import OptionsMenu from "@/components/OptionsMenu";
import { useTranslation } from "@/context/I18nProvider";
import { useAppLogo } from "@/hooks";
import MenuContent from "@/layout/Sidebar/MenuContent";
import { getUser } from "@/services";
import { dispatch, updateUser, useSelector } from "@/store";
import { Drawer } from "@/theme";
import { getMediaUrl } from "@/utils";

export default function Sidebar() {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.auth);
  const logoSrc = useAppLogo();

  const avatarUrl = getMediaUrl(user?.avatarUrl);
  // The store holds the avatar so a profile save shows here at once; this refresh covers edits made elsewhere.
  useEffect(() => {
    if (!user?._id) return;
    let cancelled = false;
    getUser(user._id)
      .then((fresh) => {
        if (!cancelled) dispatch(updateUser({ avatarUrl: fresh?.avatarUrl ?? "" }));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [user?._id]);

  return (
    <Drawer
      variant="permanent"
      sx={{
        display: { xs: "none", md: "block" },
        [`& .${drawerClasses.paper}`]: {
          backgroundColor: "background.paper",
        },
      }}
    >
      <Box
        component={Link}
        to="/"
        sx={{
          display: "flex",
          py: 1,
          alignItems: "center",
          justifyContent: "center",
          textDecoration: "none",
          minHeight: 60,
        }}
      >
        <Box component="img" src={logoSrc} alt="SkyStake" sx={{ width: "70%", height: "auto" }} />
      </Box>
      <Divider />
      <Box
        sx={{
          overflow: "auto",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <MenuContent />
      </Box>
      <Stack
        direction="row"
        sx={{
          p: 2,
          gap: 1,
          alignItems: "center",
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Avatar
          sizes="small"
          alt={user?.username}
          src={avatarUrl || undefined}
          sx={{ width: 36, height: 36, flexShrink: 0 }}
        />
        <Box sx={{ mr: "auto", minWidth: 0, overflow: "hidden" }}>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: "16px" }}>
            {t("common.welcome")}
          </Typography>
          <Typography
            variant="body2"
            noWrap
            sx={{ lineHeight: "16px", fontWeight: 600 }}
            title={user?.username ?? ""}
          >
            {user?.username ?? ""}
          </Typography>
        </Box>
        <Box sx={{ flexShrink: 0 }}>
          <OptionsMenu />
        </Box>
      </Stack>
    </Drawer>
  );
}
